import React, { useEffect, useState } from "react";
import {
  Table,
  Badge,
  Form,
  Spinner,
  Card,
  InputGroup,
  Row,
  Col,
  Pagination,
} from "react-bootstrap";
import {
  Search,
  Funnel,
  Calendar,
  Person,
  Building,
  ArrowClockwise,
} from "react-bootstrap-icons";
import axios from "axios";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

const API_BASE = "http://34.206.193.218:4001/api/admin";

/* ================= STATUS MAP ================= */
const STATUS_BADGE = {
  pending: "warning",
  scheduled: "info",
  completed: "success",
};

const ScheduleFlowCharts = () => {
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);

  const [searchTerm, setSearchTerm] = useState("");
  const [status, setStatus] = useState("");

  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const limit = 10;

  const token =
    localStorage.getItem("adminToken") || localStorage.getItem("token");

  /* ================= FETCH SCHEDULED TASKS ================= */
  const fetchTasks = async () => {
    try {
      setLoading(true);

      const res = await axios.get(
        `${API_BASE}/adminTasks/scheduleTasks`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          params: {
            page: currentPage,
            limit,
            status: status || undefined,
            search: searchTerm || undefined,
          },
        }
      );

      if (res.data.success) {
        setTasks(res.data.data || []);
        setTotalPages(res.data.pagination?.totalPages || 1);
        setTotalRecords(res.data.pagination?.totalRecords || 0);
      } else {
        throw new Error("API error");
      }
    } catch (error) {
      console.error(error);
      toast.error("Failed to fetch scheduled tasks");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTasks();
  }, [currentPage, searchTerm, status]);

  /* ================= PAGINATION ================= */
  const renderPagination = () => {
    const items = [];
    for (let i = 1; i <= totalPages; i++) {
      items.push(
        <Pagination.Item
          key={i}
          active={i === currentPage}
          onClick={() => setCurrentPage(i)}
        >
          {i}
        </Pagination.Item>
      );
    }
    return items;
  };

  return (
    <div style={{ background: "#fff", minHeight: "100vh" }}>
      <ToastContainer position="top-right" autoClose={3000} />

      {/* ===== HEADER ===== */}
      <Card className="border-0 mb-4">
        <Card.Body className="p-0">
          <Row className="align-items-center g-3">
            <Col md={4}>
              <h4 className="fw-bold mb-0">Schedule Flow</h4>
              <small className="text-muted">
                View scheduled project tasks
              </small>
            </Col>

            <Col md={4}>
              <InputGroup>
                <InputGroup.Text>
                  <Search />
                </InputGroup.Text>
                <Form.Control
                  placeholder="Search task, project or user"
                  value={searchTerm}
                  onChange={(e) => {
                    setSearchTerm(e.target.value);
                    setCurrentPage(1);
                  }}
                />
              </InputGroup>
            </Col>

            <Col md={3}>
              <InputGroup>
                <InputGroup.Text>
                  <Funnel />
                </InputGroup.Text>
                <Form.Select
                  value={status}
                  onChange={(e) => {
                    setStatus(e.target.value);
                    setCurrentPage(1);
                  }}
                >
                  <option value="">All Status</option>
                  <option value="pending">Pending</option>
                  <option value="scheduled">Scheduled</option>
                  <option value="completed">Completed</option>
                </Form.Select>
              </InputGroup>
            </Col>

            <Col md={1} className="text-end">
              <span
                role="button"
                title="Refresh"
                onClick={fetchTasks}
              >
                <ArrowClockwise />
              </span>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {/* ===== TABLE ===== */}
      <Card className="border-0 shadow-sm">
        <Card.Body className="p-0">
          {loading ? (
            <div className="text-center py-5">
              <Spinner />
            </div>
          ) : (
            <>
              <div className="table-responsive">
                <Table hover className="mb-0">
                  <thead className="table-dark">
                    <tr>
                      <th>Task</th>
                      <th>Project</th>
                      <th>User</th>
                      <th>Estimated Date</th>
                      <th>Completion Date</th>
                      <th>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {tasks.length === 0 ? (
                      <tr>
                        <td
                          colSpan="6"
                          className="text-center py-4 text-muted"
                        >
                          No scheduled tasks found
                        </td>
                      </tr>
                    ) : (
                      tasks.map((task) => (
                        <tr key={task._id}>
                          <td>{task.taskName}</td>
                          <td>
                            <Building className="me-1" />
                            {task.projectName}
                          </td>
                          <td>
                            <Person className="me-1" />
                            {task.userName}
                          </td>
                          <td>
                            <Calendar className="me-1" />
                            {new Date(
                              task.estimatedDate
                            ).toLocaleDateString()}
                          </td>
                          <td>
                            {task.completionDate
                              ? new Date(
                                  task.completionDate
                                ).toLocaleDateString()
                              : "-"}
                          </td>
                          <td>
                            <Badge bg={STATUS_BADGE[task.status]}>
                              {task.status}
                            </Badge>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </Table>
              </div>

              {/* PAGINATION */}
              {totalPages > 1 && (
                <div className="p-3 d-flex justify-content-between align-items-center">
                  <small className="text-muted">
                    Showing {(currentPage - 1) * limit + 1}–
                    {Math.min(currentPage * limit, totalRecords)} of{" "}
                    {totalRecords}
                  </small>
                  <Pagination className="mb-0">
                    {renderPagination()}
                  </Pagination>
                </div>
              )}
            </>
          )}
        </Card.Body>
      </Card>
    </div>
  );
};

export default ScheduleFlowCharts;
